import { useEffect, useRef, useState } from 'react';
import Cropper, { type Area, type Point, type Size } from 'react-easy-crop';

import {
  mimeDeSaida,
  nomeDoRecorte,
  recortarImagem,
  type MimeDeSaida,
} from '@/shared/lib/recorteDeImagem';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

const ZOOM_MAXIMO = 3;

/**
 * Enquadramento ao estilo WhatsApp/Instagram: a pessoa manda a imagem no
 * tamanho que tiver — foto de câmera, print, logo exportado torto — e escolhe
 * AQUI o que fica dentro do quadro. O servidor só vê o resultado já na
 * proporção certa.
 *
 * ⚠️ ESTE DIÁLOGO É O QUE FAZ `PROPORCAO_INVALIDA` NUNCA CHEGAR NA TELA.
 * A validação do servidor continua existindo (ela é a regra, e um POST direto
 * não passa por aqui), mas quem usa a interface não deveria mais vê-la.
 *
 * ⚠️ O ZOOM DESCE ABAIXO DE 1, E ISSO NÃO É DETALHE — É O CASO PRINCIPAL DO
 * LOGO. Um logo empilhado (marca em cima, nome embaixo, assinatura embaixo do
 * nome) chega perto de 2:1; espremê-lo num quadro 3:1 por RECORTE decepa a
 * marca ou a assinatura, e não existe posição que salve. O que ele precisa é
 * caber inteiro com sobra nas laterais — por isso `zoomMinimo` é calculado
 * para a imagem INTEIRA caber no quadro, e a sobra vira fundo no arquivo
 * final. Travar em 1, como um cropper de avatar faz, torna o diálogo inútil
 * justamente para o que ele foi criado.
 *
 * A `objectURL` nasce e morre DENTRO do efeito, nunca num inicializador de
 * `useState`. Em `StrictMode` o React monta, desmonta e remonta: a limpeza
 * revogaria a URL e o inicializador não roda de novo, então o `<img>` do
 * cropper ficaria apontando para um blob morto — tela preta e
 * `ERR_FILE_NOT_FOUND` no console. Criando no efeito, a remontagem cria outra.
 */
export function RecorteDeImagemDialogo({
  aberto,
  aoFechar,
  arquivo,
  proporcao,
  larguraSaida,
  mime,
  redonda,
  encaixar,
  assunto = 'imagem',
  enviando,
  aoConfirmar,
}: {
  aberto: boolean;
  aoFechar: () => void;
  /** O arquivo escolhido, cru. O diálogo cuida da `objectURL`. */
  arquivo: File;
  /** Largura/altura exigida pela posição — 3 para o logo, 1 para o quadrado. */
  proporcao: number;
  /**
   * Largura do arquivo gerado. Tem que ser >= a `larguraMinima` do perfil no
   * servidor, senão o enquadramento troca `PROPORCAO_INVALIDA` por
   * `IMAGEM_PEQUENA_DEMAIS` e não resolve nada.
   */
  larguraSaida: number;
  /** Omitido = PNG ou JPEG conforme a origem. Ver `mimeDeSaida`. */
  mime?: MimeDeSaida;
  /** Avatar: mostra o quadro como círculo. O arquivo continua retangular. */
  redonda?: boolean;
  /**
   * Abre com a imagem INTEIRA dentro do quadro, sobrando fundo nas laterais,
   * em vez de preenchendo. É o certo para logo — marca cortada é marca errada.
   * Para foto e imagem de produto o padrão (preencher) é melhor: ali sobra de
   * fundo é moldura à toa. Nos dois casos o controle percorre a faixa inteira;
   * isto só decide onde ele começa.
   */
  encaixar?: boolean;
  /** Só muda o texto — "Ajustar foto" x "Ajustar imagem". */
  assunto?: 'foto' | 'imagem';
  enviando: boolean;
  aoConfirmar: (arquivo: File) => void;
}) {
  const [origem, setOrigem] = useState<string | null>(null);
  const [posicao, setPosicao] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [gerando, setGerando] = useState(false);
  const [midia, setMidia] = useState<Size | null>(null);
  const [quadro, setQuadro] = useState<Size | null>(null);
  const jaEncaixou = useRef(false);

  useEffect(() => {
    const url = URL.createObjectURL(arquivo);
    setOrigem(url);
    return () => URL.revokeObjectURL(url);
  }, [arquivo]);

  /**
   * Zoom em que a imagem inteira cabe no quadro. Tudo em px de tela: `midia` é
   * o tamanho renderizado (o `react-easy-crop` já a contém no container) e
   * `quadro` é o recorte. Nunca passa de 1 — o quadro é, por construção, no
   * máximo do tamanho da mídia.
   */
  const zoomMinimo =
    midia && quadro ? Math.min(1, quadro.width / midia.width, quadro.height / midia.height) : 1;

  // Uma vez só: mexer no zoom depois disto é escolha de quem está na tela, e
  // um redimensionamento de janela não pode desfazer essa escolha.
  useEffect(() => {
    if (!encaixar || jaEncaixou.current || !midia || !quadro) return;
    jaEncaixou.current = true;
    setZoom(zoomMinimo);
  }, [encaixar, midia, quadro, zoomMinimo]);

  /**
   * Enquanto a imagem cobre o quadro, o `react-easy-crop` a mantém colada nas
   * bordas — não dá para arrastar e deixar buraco. Quando ela fica menor que o
   * quadro (o caso do logo encaixado), a trava tem que sair, senão não há como
   * posicioná-la na sobra.
   */
  const cobreOQuadro =
    !midia ||
    !quadro ||
    (midia.width * zoom >= quadro.width && midia.height * zoom >= quadro.height);

  const ocupado = enviando || gerando;

  async function aoSalvar() {
    if (!area || !origem) return;
    setGerando(true);
    try {
      const formato = mime ?? mimeDeSaida(arquivo.type);
      const blob = await recortarImagem(origem, area, {
        largura: larguraSaida,
        altura: Math.round(larguraSaida / proporcao),
        mime: formato,
      });
      aoConfirmar(new File([blob], nomeDoRecorte(arquivo.name, formato), { type: formato }));
    } finally {
      setGerando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ajustar {assunto}</DialogTitle>
          <DialogDescription>
            Arraste para posicionar. O controle diminui até a imagem caber inteira e aumenta até
            preencher — vai ser enviado o que estiver dentro do quadro claro.
          </DialogDescription>
        </DialogHeader>

        {/* `min-h` porque um quadro 3:1 num diálogo `max-w-md` teria ~130px de
            altura — pouco para arrastar. Com altura mínima o `react-easy-crop`
            desenha o recorte centralizado e escurece o resto, que é onde se vê
            o que está ficando de fora. Em 1:1 o `aspectRatio` já passa disso e
            o mínimo não muda nada. */}
        <div
          className="relative w-full min-h-64 bg-black/80 rounded-lg overflow-hidden"
          style={{ aspectRatio: proporcao }}
        >
          {origem && (
            <Cropper
              image={origem}
              crop={posicao}
              zoom={zoom}
              aspect={proporcao}
              cropShape={redonda ? 'round' : 'rect'}
              showGrid={!redonda}
              minZoom={zoomMinimo}
              maxZoom={ZOOM_MAXIMO}
              restrictPosition={cobreOQuadro}
              onMediaLoaded={({ width, height }) => setMidia({ width, height })}
              onCropSizeChange={setQuadro}
              onCropChange={setPosicao}
              onZoomChange={setZoom}
              onCropComplete={(_, areaEmPixels) => setArea(areaEmPixels)}
            />
          )}
        </div>

        <input
          type="range"
          min={zoomMinimo}
          max={ZOOM_MAXIMO}
          step={0.01}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          aria-label="Zoom"
          className="w-full accent-brand"
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={aoFechar} disabled={ocupado}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => void aoSalvar()} disabled={ocupado || !area}>
            {ocupado ? 'Enviando...' : `Usar esta ${assunto}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
